import { Component, OnInit, Input, HostBinding } from "@angular/core";
import { LoginService } from "../login.service";
import { PathologyService } from "../pathology.service";
import { DatePipe } from "@angular/common";
import * as XLSX from "xlsx";
import { writeFile } from "xlsx";

@Component({
    selector: "app-biopsystats",
    templateUrl: "./biopsystats.component.html",
    styleUrls: ["./biopsystats.component.scss"],
    providers: [DatePipe],
    standalone: false
})
export class BiopsystatsComponent implements OnInit {
  @Input() embedded: boolean = false; // Flag to know if embedded in another component
  
  @HostBinding('class.embedded-mode') get isEmbedded() {
    return this.embedded;
  }
  report: string;
  startdate: Date = new Date(new Date().setMonth(new Date().getMonth() - 1));
  enddate: Date = new Date();
  results: any = [];
  sortDirection: 'asc' | 'desc' = 'desc'; 
  selectedDoctor: string = 'All'; // Selected doctor filter
  selectedHMO: string = 'All'; // Selected HMO filter
  isLoading: boolean = false; // Loading state for server request 

  constructor(
    public loginService: LoginService,
    private pathologyService: PathologyService,
    private datePipe: DatePipe
  ) {
    this.loginService.loginMissing();
  }

  ngOnInit(): void {}

  getReport(): void {
    if (!this.report || !this.startdate || !this.enddate) {
      return;
    }
    
    this.results = []; 
    this.selectedDoctor = 'All';
    this.selectedHMO = 'All'; 
    this.isLoading = true; 
    
    this.pathologyService
      .getBioPolyStatsForColonoscopy(this.report, this.startdate, this.enddate)
      .subscribe(
        (data) => {
          this.results = data;
          this.isLoading = false; 
        },
        (error) => {
          console.error('Error fetching biopsy stats:', error);
          this.results = [];
          this.isLoading = false; 
        }
      );
  }

  getParams() {
    if (this.results && this.results.length > 0) {
      return Object.keys(this.results[0]);
    }
  }

  exportToExcel(): void {
    if (!this.results || this.results.length === 0) {
      return;
    }

    // Format date range for title
    const formatDate = (d: Date) => {
      if (!d) return '';
      return this.datePipe.transform(d, 'dd-MM-yyyy') || '';
    };

    // Create title
    let title = `Biopsy and Polypectomy Statistics - ${this.getReportTypeName()}`;
    title += `  From ${formatDate(this.startdate)} until ${formatDate(this.enddate)}`;
    
    // Add doctor filter info if not "All"
    if (this.report === 'detailed_report' && this.selectedDoctor !== 'All') {
      title += `  - Doctor: ${this.selectedDoctor}`;
    }
    // Add HMO filter info if not "All"
    if (this.report === 'detailed_report' && this.selectedHMO !== 'All') {
      title += `  - HMO: ${this.selectedHMO}`;
    }

    // Use filtered results for export
    const dataToExport = this.report === 'detailed_report' ? this.getFilteredResults() : this.results;

    // Prepare data rows
    const headers = this.getParams();
    const rows = [headers];
    
    dataToExport.forEach((result) => {
      const row = headers.map(header => {
        const value = result[header];
        // Format ExamDate column
        if (header === 'ExamDate' && value) {
          return this.datePipe.transform(value, 'dd/MM/yyyy HH:mm') || value;
        }
        // HMO column - show empty instead of '-'
        if (header === 'HMO') {
          return value || '';
        }
        return value !== null && value !== undefined ? value : '-';
      });
      rows.push(row);
    });

    // Add summary row for detailed_report
    if (this.report === 'detailed_report') {
      const summaryRow = this.getSummaryRow();
      if (summaryRow) {
        const row = headers.map(header => summaryRow[header] || '');
        rows.push(row);
      }
    }

    // Create worksheet with title row at the top
    const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([[title]]);
    XLSX.utils.sheet_add_aoa(worksheet, rows, { origin: 'A2' });

    // Merge the title row across all columns
    const merge = { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } };
    if (!worksheet['!merges']) worksheet['!merges'] = [];
    worksheet['!merges'].push(merge);

    // Style the title row
    const titleCell = worksheet['A1'];
    if (titleCell) {
      titleCell.s = {
        font: { bold: true, sz: 14 },
        alignment: { horizontal: 'center', vertical: 'center' }
      };
    }

    // Create workbook and add worksheet
    const workbook: XLSX.WorkBook = { Sheets: { 'Stats': worksheet }, SheetNames: ['Stats'] };
    
    // Generate filename with date
    const fileName = `Biopsy_Stats_${this.report}_${formatDate(this.startdate)}_to_${formatDate(this.enddate)}.xlsx`;
    
    // Write file
    writeFile(workbook, fileName);
  }

  getReportTypeName(): string {
    const reportTypes = {
      'biopsy': 'Biopsy Count',
      'biopsy_only': 'Biopsy Only (No Polypectomy)',
      'polypectomy': 'Polypectomy Count',
      'polypectomy_only': 'Polypectomy Only (No Biopsy)',
      'polypectomy_with_biopsy': 'Polypectomy and/or Biopsy',
      'both': 'Both Biopsy and Polypectomy',
      'detailed_report': 'Detailed Report - All Exams'
    };
    return reportTypes[this.report] || this.report;
  }

  sortByDate(): void {
    if (!this.results || this.results.length === 0) {
      return;
    }

    // Toggle sort direction
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';

    // Sort results by ExamDate
    this.results.sort((a, b) => {
      const dateA = new Date(a.ExamDate).getTime();
      const dateB = new Date(b.ExamDate).getTime();

      if (this.sortDirection === 'asc') {
        return dateA - dateB; // Ascending
      } else {
        return dateB - dateA; // Descending
      }
    });
  }

  getSummaryRow(): any {
    if (!this.results || this.results.length === 0 || this.report !== 'detailed_report') {
      return null;
    }

    // Use filtered results for summary
    const dataToSummarize = this.getFilteredResults();

    const summary = {
      FirstName: 'TOTAL',
      LastName: '',
      PatientID: '',
      ExamDate: '',
      HMO: '',
      ExamType: '',
      DoctorName: '',
      HasPolypectomy: '',
      HasBiopsy: '',
      HasBoth: '',
      NumberOfPolyps: ''
    };

    const totalExams = dataToSummarize.length;
    
    // Count by ExamType
    const examTypes = {};
    dataToSummarize.forEach(r => {
      const type = r.ExamType || 'Unknown';
      examTypes[type] = (examTypes[type] || 0) + 1;
    });
    
    // Count by HMO
    const hmos = {};
    dataToSummarize.forEach(r => {
      const hmo = r.HMO || 'No HMO';
      hmos[hmo] = (hmos[hmo] || 0) + 1;
    });
    
    // Count Yes/No for each pathology type
    const bothCount = dataToSummarize.filter(r => r.HasBoth === 'Yes').length;
    const onlyPolyCount = dataToSummarize.filter(r => r.HasPolypectomy === 'Yes' && r.HasBiopsy === 'No').length;
    const onlyBiopsyCount = dataToSummarize.filter(r => r.HasBiopsy === 'Yes' && r.HasPolypectomy === 'No').length;
    
    // Build summary string
    const examTypeStr = Object.entries(examTypes).map(([type, count]) => `${type}: ${count}`).join(', ');
    const hmoStr = Object.entries(hmos).map(([hmo, count]) => `${hmo}: ${count}`).join(', ');
    
    summary.LastName = `Total Exams: ${totalExams}`;
    summary.ExamType = examTypeStr;
    summary.HMO = hmoStr;
    summary.HasBoth = `Both: ${bothCount}`;
    summary.HasPolypectomy = `Only Polypectomy: ${onlyPolyCount}`;
    summary.HasBiopsy = `Only Biopsy: ${onlyBiopsyCount}`;

    return summary;
  }

  getUniqueDoctors(): string[] {
    if (!this.results || this.results.length === 0) {
      return [];
    }

    const doctors = new Set<string>();
    this.results.forEach(result => {
      if (result.DoctorName) {
        doctors.add(result.DoctorName);
      }
    });

    return Array.from(doctors).sort();
  }

  getFilteredResults(): any[] {
    if (!this.results || this.results.length === 0) {
      return [];
    }

    let filtered = this.results;

    // Filter by doctor
    if (this.selectedDoctor !== 'All') {
      filtered = filtered.filter(result => result.DoctorName === this.selectedDoctor);
    }

    // Filter by HMO
    if (this.selectedHMO !== 'All') {
      filtered = filtered.filter(result => result.HMO === this.selectedHMO);
    }

    return filtered;
  }

  getUniqueHMOs(): string[] {
    if (!this.results || this.results.length === 0) {
      return [];
    }

    const hmos = new Set<string>();
    this.results.forEach(result => {
      if (result.HMO) {
        hmos.add(result.HMO);
      }
    });

    return Array.from(hmos).sort();
  }
}
